/* eslint-disable @next/next/no-img-element */
// HERO "Autoral" (modelo_busca = 11): hero do CORRETOR PESSOAL — foto/vídeo do
// tenant em tela cheia, SEM véu pesado (a foto é o retrato de quem atende e
// precisa aparecer), com:
//  - link discreto "Referência" (lupa) acima da barra, que abre um campo de
//    CÓDIGO do imóvel e leva direto pra listagem filtrada por ele;
//  - BARRA DE BUSCA EM UMA LINHA com rótulo claro ACIMA de cada campo
//    (Negócio · Tipo do Imóvel · Valor mínimo · Valor máximo · Cidade) e botão
//    sólido "Pesquisar";
//  - ASSINATURA manuscrita abaixo da barra ('Caveat'), que é a frase da home do
//    tenant (site.frase_busca) — o traço pessoal do modelo.
// A busca (estado/URL/tracking Meta) vem inteira do hook compartilhado
// useHeroSearch (M2); este componente só desenha os controles.
// Cores 100% tokenizadas (var(--corN, #fallback)); onde o fundo é cor do tenant,
// a cor do texto é CALCULADA (isColorLight + ensureReadableTextColor). O texto
// sobre a foto usa branco porque o gradiente da base garante o fundo escuro.
// Default export = ErrorBoundary com fallback (padrão WelcomeReserva/Dwello).
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { ensureReadableTextColor, isColorLight, removerAcentos } from '../../../utils/FunctionsGlobals';
import { getHeroVideoPoster, getTenantVideoCover, useHeroVideoReady } from '../../../utils/heroVideo';
import HeroVideoCover from '../../utils/HeroVideoCover';
import type { HeroSectionProps } from '../../registry/types';
import { useHeroSearch } from '../../shared/hero-search/useHeroSearch';
import CampoSelecao from '../../shared/hero-search/CampoSelecao';
import CardsBusca from '../../shared/cards-busca/CardsBusca';
import DrawerComponent from '../../drawer/DrawerComponent';
import styles from './WelcomeAutoral.module.css';
import { usePreviaBuilder } from '../../../lib/previaBuilder';

const stripHtml = (value: unknown): string =>
    String(value ?? '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

// Milhar com ponto SEM Intl: toLocaleString depende do ICU do runtime e pode
// divergir entre servidor e navegador → hydration mismatch (React #418/#423).
const agrupaMilhar = (digitos: string): string =>
    digitos.replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const WelcomeAutoralInner: React.FC<HeroSectionProps> = ({
    allCities,
    allTypes,
    allTransacao,
    site,
    urlBanner,
    origin,
    codsite2,
    setActiveDrawer,
    activeDrawer,
    favorito,
    isMobile1,
    props,
    allCards,
}) => {
    const { videoParado } = usePreviaBuilder(); // prévia dos cards do CRM: vídeo parado na capa
    const {
        transacoes,
        transacao,
        setTransacao,
        cidade,
        setCidade,
        tipo,
        setTipo,
        precoMin,
        setPrecoMin,
        precoMax,
        setPrecoMax,
        loading,
        setLoading,
        onSubmit,
        finalize,
    } = useHeroSearch({ site, allCities, allTypes, allTransacao, codsite2, origin });
    const router = useRouter();

    // Busca por REFERÊNCIA: fica fechada e só abre no clique (é o comportamento
    // da referência do modelo — o campo de código não disputa espaço com a barra).
    const [refAberta, setRefAberta] = useState(false);
    const [codigo, setCodigo] = useState('');

    const opcoesTransacao = useMemo(
        () => transacoes.map((t) => ({ label: t.label, value: String(t.value).toLowerCase() })),
        [transacoes]
    );

    // ---- cores: papéis da paleta do catálogo; o hex do CSS é a rede de segurança ----
    const acao = site?.cor2 || '#111823';
    const corAcaoTexto =
        ensureReadableTextColor(isColorLight(acao) ? site?.cor11 : site?.cor13, acao) || '#ffffff';
    const campo = site?.cor12 || '#ffffff';
    const corCampoTexto =
        ensureReadableTextColor(isColorLight(campo) ? site?.cor11 : site?.cor13, campo) || '#3c4858';

    // ---- mídia de fundo: vídeo do tenant (fora do mobile) > banner "topo" > degradê ----
    const bannerSrc = urlBanner && urlBanner !== 'undefined' ? String(urlBanner).replaceAll('"', '') : '';
    const videoSrc = site?.url_video && !isMobile1 ? String(site.url_video).replaceAll('"', '') : '';
    const heroPoster = getHeroVideoPoster(site?.url_video);
    const heroVideoReady = useHeroVideoReady();
    // No celular o vídeo continua desligado, mas a CAPA dele entra no lugar do degradê
    // (#1271): pôster do vídeo regional > pôster do vídeo padrão > capa do vídeo do tenant.
    const capaVideo = site?.url_video_poster || heroPoster || getTenantVideoCover(site?.url_video);

    const assinatura = site?.frase_busca ? stripHtml(site.frase_busca) : '';
    const moeda = site?.moeda_tipo || 'R$';

    // A rota de listagem aceita o código como 7º segmento
    // (/imoveis/<transacao>/<cidade>/<bairro>/<empreendimento>/<tipo>/<codigo>).
    const buscarPorCodigo = () => {
        const termo = codigo.trim();
        if (!termo || loading) return;
        setLoading(true);
        const alvo = finalize(`/imoveis/${transacao}/-/-/-/-/${removerAcentos(termo).replaceAll(' ', '%20')}`);
        // router.push (não window.location): navegação client-side, igual ao onSubmit
        // do hook. Falhou, destrava o botão em vez de deixar "Buscando..." pra sempre.
        router.push(alvo).catch(() => setLoading(false));
    };

    return (
        <section className={styles.autoral} data-builder-section="busca">
            <DrawerComponent
                site={site}
                activeDrawer={activeDrawer}
                setActiveDrawer={setActiveDrawer}
                favorito={favorito || []}
                menus={props?.menus}
            />

            {/* ---------- mídia de fundo ---------- */}
            <div className={styles.media} aria-hidden="true">
                {videoSrc ? (
                    <video
                        className={styles.mediaEl}
                        autoPlay={!videoParado}
                        muted
                        loop
                        playsInline
                        preload="none"
                        poster={heroPoster}
                        src={heroPoster ? (heroVideoReady ? videoSrc : undefined) : videoSrc}
                    >
                        <track kind="captions" label="Legenda" src="legenda.vtt" />
                    </video>
                ) : bannerSrc ? (
                    <img className={styles.mediaEl} src={bannerSrc} alt="" decoding="async" />
                ) : (
                    <HeroVideoCover
                        src={capaVideo}
                        className={styles.mediaEl}
                        fallbackClassName={styles.mediaFallback}
                    />
                )}
                {/* gradiente só na base: o retrato do corretor continua visível em cima. */}
                <div className={styles.scrim} />
            </div>

            {/* ---------- conteúdo ---------- */}
            <div className={styles.content}>
                {/* linha da busca por referência (código) */}
                <div className={styles.refRow}>
                    {refAberta ? (
                        <div className={styles.refBox} style={{ background: campo }}>
                            <label htmlFor="autoral-codigo" className={styles.visuallyHidden}>
                                Código ou referência do imóvel
                            </label>
                            <input
                                id="autoral-codigo"
                                className={styles.refInput}
                                style={{ color: corCampoTexto }}
                                type="text"
                                autoFocus
                                placeholder="Código do imóvel"
                                value={codigo}
                                onChange={(e) => setCodigo(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        buscarPorCodigo();
                                    }
                                    if (e.key === 'Escape') setRefAberta(false);
                                }}
                            />
                            <button
                                type="button"
                                className={styles.refGo}
                                style={{ background: acao, color: corAcaoTexto }}
                                onClick={buscarPorCodigo}
                                disabled={loading || !codigo.trim()}
                                aria-label="Buscar pelo código"
                            >
                                <FiSearch aria-hidden="true" />
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className={styles.refToggle}
                            onClick={() => setRefAberta(true)}
                        >
                            <FiSearch aria-hidden="true" />
                            <span>Referência</span>
                        </button>
                    )}
                </div>

                {/* id="myLink": âncora do botão "BUSCA AVANÇADA" do topo (topo2). */}
                <form id="myLink" className={styles.searchLine} onSubmit={onSubmit}>
                    {/* div, não label: o painel de sugestões mora dentro do campo e um
                        <label> em volta de um listbox rotularia a lista inteira. */}
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>Negócio</span>
                        <div className={styles.control} style={{ background: campo }}>
                            <CampoSelecao
                                opcoes={opcoesTransacao}
                                valor={transacao}
                                onChange={setTransacao}
                                ariaLabel="Negócio"
                                className={styles.campoBusca}
                            />
                        </div>
                    </div>

                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>Tipo do Imóvel</span>
                        <div className={styles.control} style={{ background: campo }}>
                            <CampoSelecao
                                opcoes={allTypes || []}
                                valor={tipo}
                                onChange={setTipo}
                                rotuloVazio="Selecione"
                                ariaLabel="Tipo do imóvel"
                                className={styles.campoBusca}
                            />
                        </div>
                    </div>

                    <label className={styles.field}>
                        <span className={styles.fieldLabel}>Valor mínimo</span>
                        <div className={styles.control} style={{ background: campo }}>
                            <input
                                className={styles.input}
                                style={{ color: corCampoTexto }}
                                type="text"
                                inputMode="numeric"
                                placeholder={`${moeda} 0,00`}
                                value={precoMin}
                                onChange={(e) => setPrecoMin(agrupaMilhar(e.target.value.replace(/\D/g, '')))}
                                aria-label="Valor mínimo"
                            />
                        </div>
                    </label>

                    <label className={styles.field}>
                        <span className={styles.fieldLabel}>Valor máximo</span>
                        <div className={styles.control} style={{ background: campo }}>
                            <input
                                className={styles.input}
                                style={{ color: corCampoTexto }}
                                type="text"
                                inputMode="numeric"
                                placeholder={`${moeda} 0,00`}
                                value={precoMax}
                                onChange={(e) => setPrecoMax(agrupaMilhar(e.target.value.replace(/\D/g, '')))}
                                aria-label="Valor máximo"
                            />
                        </div>
                    </label>

                    <div className={`${styles.field} ${styles.fieldWide}`}>
                        <span className={styles.fieldLabel}>Cidade</span>
                        <div className={styles.control} style={{ background: campo }}>
                            <CampoSelecao
                                opcoes={allCities || []}
                                valor={cidade}
                                onChange={setCidade}
                                rotuloVazio="Selecione"
                                ariaLabel="Cidade"
                                className={styles.campoBusca}
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        className={styles.submit}
                        style={{ background: acao, color: corAcaoTexto }}
                        disabled={loading}
                    >
                        {loading ? 'Buscando...' : 'Pesquisar'}
                    </button>
                </form>

                {/* assinatura manuscrita = a frase da home do tenant */}
                {assinatura ? <p className={styles.assinatura}>{assinatura}</p> : null}

                <CardsBusca cards={allCards} site={site} variante="escuro" className={styles.cards} />
            </div>
        </section>
    );
};

class WelcomeAutoral extends React.Component<HeroSectionProps, { hasError: boolean }> {
    constructor(props: HeroSectionProps) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: degrada pro fallback sem quebrar a home.
    }
    render() {
        const { fallback, site } = this.props;
        if (this.state.hasError || !site) return fallback ?? null;
        return <WelcomeAutoralInner {...this.props} />;
    }
}

export default WelcomeAutoral;
